import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Layout from "@/components/Layout";
import { MapPin, X, ArrowRight, DoorOpen } from "lucide-react";
import { FadeUp } from "@/components/Motion";

/**
 * Supplier Showcase — FINAL 15-station perimeter floor plan.
 *
 * Aug 2026 update: Prime Supplier registration closed. Showcase collapsed
 * from 25 → 15 stations. Layout:
 *   • North wall (top):    S-01 · S-02 · S-03 · S-04 · S-05
 *   • East  wall (right):  S-06 · S-07 · S-08 · S-09 · S-10
 *   • West  wall (left):   S-11 · S-12 · S-13 · S-14 · S-15
 *   • South wall (bottom): no stations
 *   • Center:              six networking cocktail tables
 *
 * When `showcase_finalized=True` the public page:
 *   - Drops the "Available / Reserved" status legend and chips.
 *   - Removes the "Reserve This Station" CTA.
 *   - Renames the header to "Final Supplier Showcase Floor Plan".
 * Admins & suppliers still see reservation state via the underlying data.
 */
const STATION_INCLUDES = [
  "Premium Modular Supplier Station",
  "Custom Branded Lightbox & Counter",
  "Supplier Directory Listing",
  "Educational Sessions",
  "Sourcing Fireside Chat",
  "Networking Lunch",
  "Business Matchmaking Opportunities",
  "Event Technology Platform",
];

// Muted industry palette — elegant, understated. Only used on stations + legend.
// DA gold accent (#A39171) marks the Display America Event Services station.
const INDUSTRY_STYLE = {
  community_partner:       { color: "#4A6B7C", label: "Community Partner" },
  construction:            { color: "#5B7C99", label: "Construction" },
  resort_operations:       { color: "#5C7F6B", label: "Resort Operations" },
  security_risk:           { color: "#9B5B5B", label: "Security / Risk Management" },
  information_technology:  { color: "#7B6A98", label: "Information Technology" },
  da_support:              { color: "#A39171", label: "Display America Event Services" },
};
const INDUSTRY_ORDER = [
  "community_partner", "construction", "resort_operations", "security_risk",
  "information_technology", "da_support",
];

// --- Pure-SVG layout (no external floorplan image). Uniform *square* tiles
// arranged evenly around the perimeter with generous corner clearance so
// tiles never crowd the corners. Numbers ascend clockwise starting at
// top-left (S-01), sweep across the north wall, down the east wall, then
// up the west wall so S-15 sits toward the top of the west wall and
// S-11 sits at the bottom. S-16 (added after the initial 15-station
// publication) is a construction-category prime placed at the very top
// of the west wall, immediately adjacent to S-15. South wall reserved
// for entry doors, backwall counters, and the Travel + Leisure host tables.
const IMG_W = 1200;
const IMG_H = 920;

// Room rectangle inset from canvas edges. Taller than before so five
// square tiles fit comfortably along the vertical walls.
const ROOM = { x: 90, y: 90, w: 1020, h: 740 };

// Two entry doors on the south wall (outer edges), leaving room for
// backwall counters + host tables in the middle.
const DOOR_W = 68;
const DOOR_L = { x: ROOM.x + 140,                    y: ROOM.y + ROOM.h - 6, w: DOOR_W, h: 12 };
const DOOR_R = { x: ROOM.x + ROOM.w - 140 - DOOR_W,  y: ROOM.y + ROOM.h - 6, w: DOOR_W, h: 12 };

// Uniform *square* station size, small enough that the top/bottom corner
// stations sit ~26px away from the adjacent perpendicular wall tiles.
const STA = { w: 74, h: 74 };

// Corner buffer — distance from a station's center to the corner of the
// room. Bigger = more visual air between the last N-wall tile and the
// first E/W-wall tile.
const CORNER = 100;

// Evenly-space n points across [a, b] with (n+1) gaps between endpoints.
const spaced = (a, b, n) => {
  const step = (b - a) / (n + 1);
  return Array.from({ length: n }, (_, i) => a + step * (i + 1));
};

const NORTH_CX = spaced(ROOM.x + CORNER, ROOM.x + ROOM.w - CORNER, 5);
const EAST_CY  = spaced(ROOM.y + CORNER, ROOM.y + ROOM.h - CORNER, 5);
// West wall carries 6 tiles: to keep roughly the same per-tile spacing
// as the north / east walls, use a tighter corner buffer on this wall
// only. Same STA size, same styling — just slightly less padding at the
// two corners so all six tiles breathe.
const WEST_CORNER = 60;
const WEST_CY  = spaced(ROOM.y + WEST_CORNER, ROOM.y + ROOM.h - WEST_CORNER, 6);
const WEST_NUMBERS = [16, 15, 14, 13, 12, 11];

const NORTH_Y = ROOM.y + STA.h / 2 + 14;                // just inside the top wall
const EAST_X  = ROOM.x + ROOM.w - STA.w / 2 - 14;       // just inside the right wall
const WEST_X  = ROOM.x + STA.w / 2 + 14;                // just inside the left wall

function buildLayout() {
  const layout = [];
  // North: S-01..S-05 (left → right)
  NORTH_CX.forEach((cx, i) => {
    layout.push({
      number: i + 1, wall: "north",
      cx, cy: NORTH_Y, w: STA.w, h: STA.h,
      x: cx - STA.w / 2, y: NORTH_Y - STA.h / 2,
    });
  });
  // East: S-06..S-10 (top → bottom)
  EAST_CY.forEach((cy, i) => {
    layout.push({
      number: 6 + i, wall: "east",
      cx: EAST_X, cy, w: STA.w, h: STA.h,
      x: EAST_X - STA.w / 2, y: cy - STA.h / 2,
    });
  });
  // West: S-16, S-15..S-11 (top → bottom).
  WEST_CY.forEach((cy, i) => {
    layout.push({
      number: WEST_NUMBERS[i], wall: "west",
      cx: WEST_X, cy, w: STA.w, h: STA.h,
      x: WEST_X - STA.w / 2, y: cy - STA.h / 2,
    });
  });
  return layout;
}

const LAYOUT = buildLayout();

// Six networking cocktail tables in the center — two rows × three columns.
const NETWORKING_TABLES = (() => {
  const cxs = [ROOM.x + ROOM.w * 0.32, ROOM.x + ROOM.w * 0.5, ROOM.x + ROOM.w * 0.68];
  const cys = [ROOM.y + ROOM.h * 0.38, ROOM.y + ROOM.h * 0.60];
  const pts = [];
  for (const y of cys) for (const x of cxs) pts.push({ x, y });
  return pts;
})();

// South-wall Travel + Leisure host set-up: two backwall counters and two
// host tables in front of them.
const SOUTH_WALL = (() => {
  const wallTop = ROOM.y + ROOM.h - 6;      // inside face of the south wall
  const counterH = 26;
  const counterW = 200;
  const gap = 42;
  const centerX = ROOM.x + ROOM.w / 2;
  const counterL = { x: centerX - counterW - gap, y: wallTop - counterH, w: counterW, h: counterH };
  const counterR = { x: centerX + gap,             y: wallTop - counterH, w: counterW, h: counterH };
  const tableW = 168, tableH = 46;
  const tableL = { x: counterL.x + (counterW - tableW) / 2, y: counterL.y - tableH - 22, w: tableW, h: tableH,
                    line1: "TRAVEL + LEISURE", line2: "SOURCING TEAM" };
  const tableR = { x: counterR.x + (counterW - tableW) / 2, y: counterR.y - tableH - 22, w: tableW, h: tableH,
                    line1: "TRAVEL + LEISURE", line2: "GO" };
  return { counterL, counterR, tableL, tableR };
})();

export default function Venue() {
  const { user } = useAuth();
  const [stations, setStations] = useState([]);
  const [event, setEvent] = useState(null);
  const [selected, setSelected] = useState(null);
  const [myStationId, setMyStationId] = useState(null);

  useEffect(() => {
    api.get("/stations?all=1").then(({ data }) => setStations(data.stations || []));
    api.get("/event").then(({ data }) => setEvent(data)).catch(() => {});
    if (user?.role === "exhibitor") {
      api.get("/exhibitors/me/station").then(({ data }) => setMyStationId(data.station?.id || null)).catch(() => {});
    }
  }, [user]);

  const stationsByNumber = useMemo(() => {
    const m = {};
    for (const s of stations) m[s.number] = s;
    return m;
  }, [stations]);

  const finalized = event?.showcase_finalized === true;

  return (
    <Layout>
      {/* HERO */}
      <section className="border-b border-[hsl(var(--line))]">
        <div className="container-summit py-12">
          <FadeUp className="overline mb-4"><span className="accent-rule" />Prime Supplier Showcase</FadeUp>
          <FadeUp delay={0.05}>
            <h1 className="serif text-4xl sm:text-5xl">Prime Supplier Showcase</h1>
          </FadeUp>
          <FadeUp delay={0.1} className="mt-4 text-zinc-500 max-w-3xl leading-relaxed">
            The Prime Supplier Showcase features branded supplier stations arranged along the perimeter
            of an open networking environment designed to facilitate Business Matchmaking, networking,
            and collaboration throughout the afternoon program. Invited Small Business Enterprises (SBEs)
            and Travel + Leisure leaders participate throughout the showcase.
          </FadeUp>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="border-b border-[hsl(var(--line))]">
        <div className="container-summit py-10">
          <div className="overline mb-5"><span className="accent-rule" />Prime Supplier Showcase Includes</div>
          <ul className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-3 text-sm text-zinc-700" data-testid="venue-value-panel">
            {STATION_INCLUDES.map((it) => (
              <li key={it} className="flex items-start gap-3">
                <span className="text-[#A39171] font-semibold shrink-0">✓</span>
                {it}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FLOOR PLAN + DETAIL */}
      <section className="container-summit py-10 grid lg:grid-cols-[2fr_1fr] gap-8">
        <div className="card-flat p-4 md:p-6">
          <div className="flex items-baseline justify-center mb-4">
            <div className="overline text-center" data-testid="floorplan-header">
              Supplier Showcase Floor · Heartbeat Rooms 3–4
            </div>
          </div>

          <div className="relative w-full" style={{ aspectRatio: `${IMG_W} / ${IMG_H}` }}>
            <svg viewBox={`0 0 ${IMG_W} ${IMG_H}`}
                 className="w-full h-full block select-none"
                 preserveAspectRatio="xMidYMid meet"
                 role="img" aria-label="Supplier Showcase floor plan">
              {/* Room outline */}
              <rect x={ROOM.x} y={ROOM.y} width={ROOM.w} height={ROOM.h}
                    fill="#FAFAF7" stroke="#1F1F1F" strokeWidth="2"
                    data-testid="floorplan-room" />
              {/* Two entry doors on the south wall */}
              {[DOOR_L, DOOR_R].map((d, i) => (
                <g key={`door-${i}`} data-testid={`floorplan-door-${i + 1}`}>
                  <rect x={d.x} y={d.y} width={d.w} height={d.h}
                        fill="#FAFAF7" stroke="#FAFAF7" strokeWidth="4" />
                  <text x={d.x + d.w / 2} y={d.y + 34}
                        fontSize="11" fill="#666" textAnchor="middle"
                        letterSpacing="2.5" fontWeight="500">ENTRY</text>
                </g>
              ))}

              {/* Wall compass labels */}
              <text x={ROOM.x + ROOM.w / 2} y={ROOM.y - 22}
                    fontSize="12" fill="#666" textAnchor="middle"
                    letterSpacing="4" fontWeight="500">NORTH WALL</text>
              <text x={ROOM.x + ROOM.w + 22} y={ROOM.y + ROOM.h / 2}
                    fontSize="12" fill="#666" textAnchor="middle"
                    letterSpacing="4" fontWeight="500"
                    transform={`rotate(90, ${ROOM.x + ROOM.w + 22}, ${ROOM.y + ROOM.h / 2})`}>EAST WALL</text>
              <text x={ROOM.x - 22} y={ROOM.y + ROOM.h / 2}
                    fontSize="12" fill="#666" textAnchor="middle"
                    letterSpacing="4" fontWeight="500"
                    transform={`rotate(-90, ${ROOM.x - 22}, ${ROOM.y + ROOM.h / 2})`}>WEST WALL</text>
              <text x={ROOM.x + ROOM.w / 2} y={ROOM.y + ROOM.h + 34}
                    fontSize="12" fill="#666" textAnchor="middle"
                    letterSpacing="4" fontWeight="500">SOUTH WALL</text>

              {/* South-wall backwall counters + T+L host tables */}
              {[SOUTH_WALL.counterL, SOUTH_WALL.counterR].map((c, i) => (
                <rect key={`counter-${i}`}
                      x={c.x} y={c.y} width={c.w} height={c.h}
                      fill="#EEEAE1" stroke="#8A8577" strokeWidth="1.5"
                      data-testid={`floorplan-counter-${i + 1}`} />
              ))}
              {[SOUTH_WALL.tableL, SOUTH_WALL.tableR].map((t, i) => (
                <g key={`host-${i}`} data-testid={`floorplan-host-table-${i + 1}`}>
                  <rect x={t.x} y={t.y} width={t.w} height={t.h} rx="4"
                        fill="#1F1F1F" stroke="#1F1F1F" strokeWidth="1.5" />
                  <text x={t.x + t.w / 2} y={t.y + 17}
                        fontSize="10" fill="#A39171" textAnchor="middle"
                        letterSpacing="2" fontWeight="600">{t.line1.toUpperCase()}</text>
                  <text x={t.x + t.w / 2} y={t.y + 33}
                        fontSize="10" fill="#F5F1E9" textAnchor="middle"
                        letterSpacing="1.5" fontWeight="600">{t.line2.toUpperCase()}</text>
                </g>
              ))}

              {/* Networking cocktail tables (informational only) */}
              {NETWORKING_TABLES.map((t, i) => (
                <g key={`nt-${i}`} data-testid={`networking-table-${i + 1}`}>
                  <circle cx={t.x} cy={t.y} r="30"
                          fill="#EEEAE1" stroke="#A39171" strokeWidth="1.5"
                          strokeDasharray="3 3" />
                  <text x={t.x} y={t.y + 4}
                        fontSize="10" fill="#A39171" textAnchor="middle"
                        letterSpacing="1.5" fontWeight="600">COCKTAIL</text>
                </g>
              ))}

              {/* Interactive station rectangles */}
              {LAYOUT.map((slot) => {
                const st = stationsByNumber[slot.number];
                const assigned = !!(st && st.exhibitor_id);
                const isMine = !!(st && myStationId && st.id === myStationId);
                const industry = (st && st.industry_category) || null;
                const industryColor = INDUSTRY_STYLE[industry]?.color || "#C7BFA9";

                let fillColor, fillOpacity, strokeColor, strokeWidth;
                if (isMine) {
                  fillColor = "#A39171"; fillOpacity = 0.95; strokeColor = "#000"; strokeWidth = 3;
                } else if (assigned) {
                  fillColor = industryColor; fillOpacity = 0.9; strokeColor = "#1F1F1F"; strokeWidth = 1.5;
                } else {
                  fillColor = "#F5F1E9"; fillOpacity = 1; strokeColor = "#C7BFA9"; strokeWidth = 1.5;
                }

                const label = `S-${String(slot.number).padStart(2, "0")}`;
                return (
                  <g key={slot.number}
                     className="cursor-pointer group"
                     onClick={() => setSelected(st || { number: slot.number, label, exhibitor_id: null, industry_category: industry })}
                     data-testid={`station-${label.slice(2)}`}
                     data-station-number={slot.number}
                     data-station-wall={slot.wall}
                     data-station-status={isMine ? "mine" : assigned ? "reserved" : "available"}>
                    <rect x={slot.x} y={slot.y} width={slot.w} height={slot.h}
                          rx="4" ry="4"
                          fill={fillColor} fillOpacity={fillOpacity}
                          stroke={strokeColor} strokeWidth={strokeWidth}
                          strokeDasharray={assigned || isMine ? "0" : "4 3"}
                          className="transition-all duration-200 group-hover:stroke-black group-hover:stroke-[2.5px]" />
                    <text x={slot.cx} y={slot.cy + 4}
                          fontSize="12"
                          fill={assigned || isMine ? "#fff" : "#8A8577"}
                          textAnchor="middle" fontWeight="700"
                          pointerEvents="none">
                      {label}
                    </text>
                  </g>
                );
              })}
            </svg>
          </div>

          {/* Industry legend */}
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2.5 text-[11px] uppercase tracking-[0.16em] text-zinc-600"
               data-testid="industry-legend">
            {INDUSTRY_ORDER.map((k) => (
              <IndustryLegend key={k} color={INDUSTRY_STYLE[k].color} label={INDUSTRY_STYLE[k].label} testId={`legend-${k}`} />
            ))}
          </div>

          {/* Status legend row — hidden. Available/Reserved chips retired
              once the showcase is finalized (station tiles + industry
              legend already convey occupancy). */}
          {myStationId && (
            <div className="mt-4 pt-4 border-t border-zinc-200 flex flex-wrap gap-5 text-[11px] uppercase tracking-[0.18em] text-zinc-500">
              <StatusLegend swatch="bg-[#A39171]" label="Your Station" />
            </div>
          )}

          {/* Disclaimer */}
          <p className="mt-5 text-[11px] text-zinc-500 leading-relaxed" data-testid="floorplan-disclaimer">
            Illustration only. Supplier Showcase layout is representative and not to scale.
            Final layout may be adjusted based on venue requirements.
          </p>
        </div>

        {/* Detail panel */}
        <div className="card-flat p-6 self-start sticky top-24" data-testid="station-detail">
          <AnimatePresence mode="wait">
            {!selected && <EmptyDetail key="empty" />}
            {selected && !selected.exhibitor_id && (
              <AvailableDetail key={`av-${selected.number}`} station={selected} finalized={finalized} onClose={() => setSelected(null)} />
            )}
            {selected && selected.exhibitor_id && selected.exhibitor && (
              <ReservedDetail key={selected.id} station={selected} finalized={finalized} onClose={() => setSelected(null)} />
            )}
          </AnimatePresence>
        </div>
      </section>
    </Layout>
  );
}

/* ────────── Detail panels ────────── */

function EmptyDetail() {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <MapPin size={20} className="text-[#A39171]" strokeWidth={1.4} />
      <div className="serif text-xl mt-4">Select a station</div>
      <p className="text-sm text-zinc-500 mt-2">Tap any station on the floor plan to view details.</p>
      <div className="mt-6 flex items-start gap-3 text-xs text-zinc-500">
        <DoorOpen size={14} className="mt-0.5 shrink-0 text-[#A39171]" strokeWidth={1.4} />
        Perimeter layout. Center reserved for open networking cocktail tables.
      </div>
    </motion.div>
  );
}

function AvailableDetail({ station, finalized, onClose }) {
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
      <button onClick={onClose} className="text-zinc-400 hover:text-black float-right" aria-label="Close" data-testid="station-detail-close"><X size={16} /></button>
      <div className="overline">Station {station.label || `S-${String(station.number).padStart(2, "0")}`}</div>
      <IndustryPill category={station.industry_category} />
      {finalized ? (
        <div className="serif text-2xl mt-3" data-testid="station-status">Available</div>
      ) : (
        <>
          <div className="serif text-2xl mt-3" data-testid="station-status">Available</div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mt-3">Status · Ready to reserve</div>
          <p className="text-sm text-zinc-500 mt-4 leading-relaxed">
            This station may be reserved during Prime Supplier registration.
          </p>
          <Link to="/onboarding" className="btn-black mt-6 h-11 text-xs w-full justify-center" data-testid="station-cta-register">
            Reserve This Station <ArrowRight size={14} />
          </Link>
        </>
      )}
    </motion.div>
  );
}

function ReservedDetail({ station, finalized, onClose }) {
  const ex = station.exhibitor;
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
      <button onClick={onClose} className="text-zinc-400 hover:text-black float-right" aria-label="Close" data-testid="station-detail-close"><X size={16} /></button>
      <div className="overline">Station {station.label}</div>
      <IndustryPill category={ex.industry_category || station.industry_category} />
      {!finalized && (
        <div className="text-[11px] uppercase tracking-[0.18em] text-emerald-700 mt-3" data-testid="station-status">Reserved</div>
      )}

      {ex.logo_file_id ? (
        <div className="h-12 mt-4 flex items-center">
          <img src={`${BACKEND_URL}/api/files/${ex.logo_file_id}`}
               alt={ex.company_name}
               className="max-h-12 w-auto object-contain" />
        </div>
      ) : ex.is_da_support ? (
        <div className="h-12 mt-4 flex items-center bg-white px-3 border border-zinc-200">
          <img src="/brand/display-america-logo.png"
               alt="Display America"
               className="max-h-10 w-auto object-contain" />
        </div>
      ) : null}

      <div className="serif text-2xl mt-3" data-testid="station-company-name">{ex.company_name}</div>

      <dl className="mt-4 space-y-2.5 text-sm">
        {ex.primary_contact && (
          <Row label="Primary Contact" value={ex.primary_contact} sub={ex.contact_title} />
        )}
        {ex.website && (
          <Row label="Website" value={
            <a href={/^https?:\/\//.test(ex.website) ? ex.website : `https://${ex.website}`}
               target="_blank" rel="noreferrer"
               className="text-[#A39171] hover:text-black underline underline-offset-4 break-all">
              {ex.website.replace(/^https?:\/\//, "")}
            </a>
          } />
        )}
        {ex.products_services && (
          <Row label="Products & Services" value={<span className="text-zinc-700">{ex.products_services}</span>} />
        )}
        {ex.business_areas?.length > 0 && (
          <Row label="Business Matchmaking Interests"
               value={<span className="text-zinc-700">{ex.business_areas.join(" · ")}</span>} />
        )}
      </dl>

      <Link to={`/supplier/${ex.id}`}
            className="btn-outline-black mt-6 h-11 text-xs w-full justify-center"
            data-testid="station-cta-profile">
        View Supplier Profile <ArrowRight size={14} />
      </Link>
    </motion.div>
  );
}

function Row({ label, value, sub }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">{label}</dt>
      <dd className="text-sm text-black mt-0.5">
        {value}
        {sub && <div className="text-xs text-zinc-500 mt-0.5">{sub}</div>}
      </dd>
    </div>
  );
}

function IndustryPill({ category }) {
  const meta = INDUSTRY_STYLE[category];
  if (!meta) return null;
  return (
    <div className="inline-flex items-center gap-2 mt-3" data-testid="station-industry-pill">
      <span className="inline-block w-2.5 h-2.5" style={{ background: meta.color }} />
      <span className="text-[10px] uppercase tracking-[0.22em] text-zinc-600">{meta.label}</span>
    </div>
  );
}

function IndustryLegend({ color, label, testId }) {
  return (
    <div className="inline-flex items-center gap-2" data-testid={testId}>
      <span className="inline-block w-3 h-3 shrink-0" style={{ background: color }} />
      <span>{label}</span>
    </div>
  );
}

function StatusLegend({ swatch, label }) {
  return (
    <div className="inline-flex items-center gap-2">
      <span className={`inline-block w-3 h-3 ${swatch}`} />
      {label}
    </div>
  );
}
